import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, ChevronRight, Loader2, PenLine, Sparkles } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { useTranslate } from "../context/TranslateContext";
import { listPublished } from "../lib/api";

const fade = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] } }),
};

const typeLabel = { article: "Article", movie: "Film", person: "Profile", song: "Music", news: "News" };

const cardFor = (doc) => {
  const langs = doc.langs || {};
  const code = langs.en ? "en" : (langs[doc.primary_lang] ? doc.primary_lang : Object.keys(langs)[0]);
  const v = langs[code] || {};
  return { id: doc.id, title: v.title || "Untitled contribution", summary: v.summary || "", author: doc.author_name || "Anonymous", type: doc.type || "article", tags: doc.tags || [] };
};

export default function CommunityLibrary() {
  const { t } = useTranslate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    let cancelled = false;
    listPublished()
      .then((d) => { if (!cancelled) setItems(d.map(cardFor)); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-paper" data-testid="community-library-page">
      <InnerNav />

      <section className="border-b border-edge">
        <div className="mx-auto max-w-[1400px] px-6 py-16 md:px-12 md:py-20">
          <motion.div initial="hidden" animate="show" variants={fade}>
            <div className="flex items-center gap-2 font-body text-xs uppercase tracking-[0.28em] text-gold">
              <BookOpen className="h-4 w-4" /> {t("Community Library")}
            </div>
            <h1 data-testid="library-title" className="mt-4 max-w-3xl font-display text-4xl font-semibold leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl">
              {t("Stories from the ")}<span className="italic text-gradient-gold">{t("community")}</span>
            </h1>
            <p className="mt-5 max-w-2xl font-body text-lg leading-relaxed text-ash md:text-xl">
              {t("Approved contributions from readers and editors — published as live, translatable articles you can also listen to.")}
            </p>
            <Link to="/contribute" data-testid="library-contribute-cta"
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-gold/50 px-5 py-2.5 font-body text-sm text-gold transition-colors hover:bg-gold/10">
              <PenLine className="h-4 w-4" /> {t("Contribute your own")}
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-14 md:px-12 md:py-16">
        {loading ? (
          <div className="flex flex-col items-center py-24 text-center" data-testid="library-loading">
            <Loader2 className="h-7 w-7 animate-spin text-gold" />
            <p className="mt-4 font-body text-sm text-ash">{t("Loading contributions…")}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-3xl border border-edge bg-surface/40 p-10 text-center" data-testid="library-empty">
            <Sparkles className="mx-auto h-8 w-8 text-gold" />
            <h2 className="mt-4 font-display text-2xl text-cream">{t("No published stories yet")}</h2>
            <p className="mt-2 font-body text-ash">{t("Once an editor approves a contribution, it will appear here as a live article.")}</p>
            <Link to="/contribute" className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 font-body font-medium text-ink transition-transform hover:scale-105">
              {t("Write the first one")} <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((it, i) => (
              <motion.div key={it.id} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} custom={i} variants={fade}>
                <Link to={`/article/cms-${it.id}`} data-testid={`library-card-${it.id}`}
                  className="group flex h-full flex-col rounded-2xl border border-edge bg-surface p-6 transition-colors duration-300 hover:border-gold/50">
                  <span className="inline-flex w-fit items-center rounded-full bg-gold/10 px-3 py-1 font-body text-xs uppercase tracking-widest text-gold">
                    {t(typeLabel[it.type] || "Article")}
                  </span>
                  <h3 className="mt-4 font-display text-2xl font-semibold leading-tight text-cream">{it.title}</h3>
                  {it.summary && <p className="mt-2 flex-1 font-body text-sm leading-relaxed text-ash line-clamp-3">{it.summary}</p>}
                  <div className="mt-5 flex items-center justify-between">
                    <span className="font-body text-xs text-ash">{t("by")} {it.author}</span>
                    <span className="inline-flex items-center gap-1 font-body text-sm text-gold">
                      {t("Read")} <ChevronRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
